import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import RaisedButton from 'material-ui/RaisedButton';
import omit from 'lodash/omit';
import PhoneNumberSearchWidget from 'components/pure/utils/phone/PhoneNumberSearchWidget';
import SearchTypeIconMenuContainer from 'components/stateful/utils/SearchTypeIconMenuContainer';
import './SearchInputBox.styl';
import ConstValues from 'utils/const';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import {RightChevronIcon} from '@components/icon/RightChevronIcon';

/**
 * A `SearchInputBox` implements a simple UI-only renderer for a search
 * box that supports throttle
 */
export default class SearchInputBox extends React.Component {
  static propTypes = {
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    throttle: PropTypes.number,
    value: PropTypes.string,
    buttonType: PropTypes.string,
    buttonText: PropTypes.string,
  };

  static defaultProps = {
    className: '',
    onChange() {},
    placeholder: 'Search',
    throttle: 0,
    buttonType: 'arrow',
    buttonText: '',
  };

  constructor(props) {
    super(props);
    this.clearSearchTerm = this.clearSearchTerm.bind(this);
    this.handleUserInitiatedSearchAction = this.handleUserInitiatedSearchAction.bind(this);
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onInputKeyPress = this.onInputKeyPress.bind(this);
    this.onTouchTapSearchButton = this.onTouchTapSearchButton.bind(this);
  }

  /**
   * Call this.updateAutocompleteSearch with an event object constructed from the `value`
   * @param  {string} value Value of the search term
   */
  updateSearchWithValue(value) {
    this.updateAutocompleteSearch(value);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.value && nextProps.value !== this.props.value) {
      this.updateSearchWithValue(nextProps.value);
    }
    if (nextProps.shouldClearSearchTerm) {
      setImmediate(() => {
        this.clearSearchTerm();
        SearchFieldsActions.clearShouldClearSearchTerm();
      });
    }
  }

  /**
   * Handle a user-initiated search action for the string `searchTerm`. A user-initiated search action could be:
   * 1) clicking the "Search" button; *or*
   * 2) hitting "Enter" on the keyboard
   * @param  {string} searchTerm A string search term
   */
  handleUserInitiatedSearchAction(searchTerm) {
    if (searchTerm && searchTerm.length > 0) {
      this.updateAndTriggerSearch(searchTerm);
    }
  }

  /**
   * Callback triggered when the user clicks the "Search" button. When this happens, we always kick-off
   * a search request to the backend if the number of characters in `this.props.searchTerm` is larger than 0.
   * Notice that this is different from the function that handles `componentWillReceiveProps` where the number
   * of characters must be larger than 2 in order to trigger a auto-complete-based search
   */
  onTouchTapSearchButton() {
    let searchTerm = this.props.searchTerm;
    if (!searchTerm || searchTerm.length === 0) {
      searchTerm = '*';
    }
    this.handleUserInitiatedSearchAction(searchTerm);
  }

  /**
   * Callback triggered when the value of the search input box <input /> changes
   * @param  {event} e An event object
   */
  onInputChange(e) {
    const searchTerm = e.target.value;
    this.updateAutocompleteSearch(searchTerm);
  }

  /**
   * Callback triggered when the user presses a key. If the user presses the "Enter" key, then we initiate a search
   * request if the number of characters in the search box is currently higher than 0
   * @param  {event} An event object
   */
  onInputKeyPress(event) {
    const key = event.key;
    const searchTerm = event.target.value;
    if (key === 'Enter') {
      this.handleUserInitiatedSearchAction(searchTerm);
    }
  }

  /**
   * Remove unknown keys from `inputProps` so that the "Uknown Prop Warning" won't be triggered
   * @param  {object} inputProps An object that contains keys which should not be passed to <input />
   * @return {object}            A clean `inputProps` object
   */
  removeUnknownKeys(inputProps) {
    return omit(inputProps, [
      `searchTermChanged`,
      `searchTerm`,
      `throttle`,
      `SearchInputActions`,
      'menuItemsStatus',
      'shouldClearSearchTerm',
      `buttonType`,
      `buttonText`,
      `onButtonClick`,
    ]);
  }

  /**
   * Return `true` if `searchTerm` should trigger an autocomplete-based search; `false` otherwise
   * @param  {string} searchTerm A string search term provided by the user
   * @return {boolean}            `true` if search should be triggered; `false` if not
   */
  shouldTriggerAutocompleteSearch(searchTerm) {
    return (
      searchTerm.length === 0 ||
      searchTerm.length >= ConstValues.minimalNumberOfCharactersToTriggerSearch ||
      searchTerm === '*'
    );
  }

  /**
   * Only when the user has typed at least 3 characters or removed all the characters do we initiate an actual
   * search request. In order words, The character range [1, 2] does not trigger actual search requests
   * @param {searchTerm} current search term
   */
  updateAutocompleteSearch(searchTerm) {
    this.props.SearchInputActions.updateSearchTerm(searchTerm);
    if (this.shouldTriggerAutocompleteSearch(searchTerm)) {
      this.triggerSearchTermChangeEvent(searchTerm);
    }
  }

  /**
   * Since a search request should be triggered, we update the searchTerm value in the relevant ReactJS store and
   * then trigger a search term changed event which will kick off a search request to the backend to fetch the latest
   * search results
   * @param  {string} value A string search term
   */
  updateAndTriggerSearch(value) {
    this.props.SearchInputActions.updateSearchTerm.defer(value);
    this.triggerSearchTermChangeEvent(value);
  }

  /**
   * Trigger a search term changed event using `this.props.onChange`
   * @param  {string} searchTerm A string search term
   */
  triggerSearchTermChangeEvent(searchTerm) {
    if (this._throttleTimeout) {
      clearTimeout(this._throttleTimeout);
    }
    this._throttleTimeout = setTimeout(() => {
      this.props.onChange(searchTerm);
    }, this.props.throttle);
  }

  /** Trigger an action to clear `this.props.searchTerm` because `searchFieldsState` might have been updated by the
   *  user for a given search-type selected in SearchTypeIconMenu
   */
  clearSearchTerm() {
    this.props.SearchInputActions.updateSearchTerm.defer('');
  }

  onButtonClick(e) {
    if (this.props.onButtonClick) {
      this.props.onButtonClick(e);
    }
  }

  render() {
    const isSearchingPhone = this.props.searchType === ConstValues.searchTypes.miPhone;
    let {searchType, ...inputProps} = this.props;
    const inputDivClassnames = classNames('search-input', {
      'search-input-phone-div': isSearchingPhone,
    });
    inputProps.value = this.props.searchTerm;
    inputProps = this.removeUnknownKeys(inputProps);
    const inputClassName = classNames([isSearchingPhone && 'search-input-phone']);
    return (
      <div className={inputDivClassnames}>
        {!isSearchingPhone ? (
          <>
            <input
              {...inputProps}
              className={inputClassName}
              onKeyPress={this.onInputKeyPress}
              onChange={this.onInputChange}
            />
            <div className="search-enter">
              <SearchTypeIconMenuContainer
                searchType={searchType}
                menuItemsStatus={this.props.menuItemsStatus}
                clearSearchTerm={this.clearSearchTerm}
                searchTerm={this.props.searchTerm}
                shouldClearSearchTerm={this.props.shouldClearSearchTerm}
              />
            </div>
          </>
        ) : (
          <>
            <div className="search-enter">
              <SearchTypeIconMenuContainer
                searchType={searchType}
                menuItemsStatus={this.props.menuItemsStatus}
                clearSearchTerm={this.clearSearchTerm}
                searchTerm={this.props.searchTerm}
                shouldClearSearchTerm={this.props.shouldClearSearchTerm}
              />
            </div>
            <PhoneNumberSearchWidget
              className={'search-input-phone-number-widget'}
              flexDivClassname={'search-input-phone-topbar-div'}
              onSearchTermChange={this.handleUserInitiatedSearchAction}
              searchTerm={this.props.searchTerm}
              isRenderedInTopBar
            />
            <RaisedButton primary={true} className="search-button" onClick={this.onButtonClick}>
              <RightChevronIcon tooltip="Search" />
            </RaisedButton>
          </>
        )}
      </div>
    );
  }
}
